import { App, Divider, Image, Spin } from "antd";
import styles from "./styles.module.scss";
import { ReactComponent as FileIcon } from "../../assets/file.svg";
import { useMutation, useQueries } from "@tanstack/react-query";
import {
  getCouponDiscount,
  getInstitutionByApplicantId,
  getJambByApplicantId,
  getPersonalInfoByApplicantId,
  getQualificationByApplicantId,
  getReferenceByApplicantId,
  getUploadNyscByApplicantId,
  getWorkHistoryByApplicantId,
} from "../../utils/request";
import { useAtom } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { formatDate } from "../../utils/formatDate";
import OnboardingButtonGroup from "./onboardingButtonGroup";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../custom";
import { routes } from "../../routes";
import { useApplicationPeriod } from "../../hooks/useApplicationPeriod";
import RegistrationForm from "./registrationForm";
import { useReactToPrint } from "react-to-print";
import { APP_BASE_URL } from "../../utils/constants";
import { usePayment } from "../../contexts/payment";
import { useGetApplicantDetailsQuery } from "../../hooks/api/useGetApplicantDetailsQuery";
import { useGetRequiredDocumentsQuery } from "../../hooks/api/useGetRequiredDocumentsQuery";
import ChangeCourseModal from "./changeCourseModal";
import ReviewPaymentDetails from "./reviewPaymentDetails";

const isBachelorApplicant = (programType?: string) =>
  programType?.trim().toLowerCase().startsWith("b") ?? false;

const isDirectEntryApplicant = (modeOfEntry?: string) =>
  modeOfEntry?.trim().toLowerCase().replace(/\s+/g, "").includes("directentry");

const isUtmeApplicant = (modeOfEntry?: string) =>
  modeOfEntry?.trim().toLowerCase().replace(/\s+/g, "").includes("utme");

const REQUIRED_DOCUMENTS_STORAGE_KEY = "required-documents-upload";

interface UploadedRequiredDocumentMeta {
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  viewUrl?: string;
}

const getStoredRequiredDocuments = () => {
  try {
    return JSON.parse(
      localStorage.getItem(REQUIRED_DOCUMENTS_STORAGE_KEY) ?? "{}",
    ) as Record<string, UploadedRequiredDocumentMeta>;
  } catch (_error) {
    return {};
  }
};

const Review = () => {
  const [storedUser, setUser] = useAtom(userAtom);
  const { applicantDetails } = useGetApplicantDetailsQuery({
    applicantId: storedUser?.applicantId,
  });
  const user = applicantDetails ?? storedUser;
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const {
    isLateApplicationPeriod,
    shouldPayLateApplicationFee,
    lateApplicationFee,
  } = useApplicationPeriod();
  const { initiatePayment, isProcessing } = usePayment();
  const [visible, setVisible] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [discountStatus, setDiscountStatus] = useState<
    "success" | "error" | ""
  >("");
  const [changeCourseModalOpen, setChangeCourseModalOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const printDocument = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Application_Review_${user?.applicantId}`,
    pageStyle: `
    @page {
      size: A4;
      margin: 20mm;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      * {
        overflow: visible !important;
        height: auto !important;
      }
    }
  `,
  });

  const { notification } = App.useApp();

  const navigate = useNavigate();
  const { documents: requiredDocuments, isLoading: isRequiredDocumentsLoading } =
    useGetRequiredDocumentsQuery({
      programTypeId: user?.programTypeId,
    });

 const queries = useQueries({
  queries: [
    {
      queryKey: ["review-queries", "get-institution", user?.applicantId],
      queryFn: () => getInstitutionByApplicantId(user?.applicantId ?? ""),
    },
    {
      queryKey: ["review-queries", "get-work-history", user?.applicantId],
      queryFn: () => getWorkHistoryByApplicantId(user?.applicantId ?? ""),
    },
    {
      queryKey: ["review-queries", "get-nysc-upload", user?.applicantId],
      queryFn: () => getUploadNyscByApplicantId(user?.applicantId ?? ""),
    },
    {
      queryKey: ["review-queries", "get-personal-info-id", user?.applicantId],
      queryFn: () => getPersonalInfoByApplicantId(user?.applicantId ?? ""),
    },
    {
      queryKey: ["review-queries", "get-all-qualification", user?.applicantId],
      queryFn: () => getQualificationByApplicantId(user?.applicantId ?? ""),
    },
    {
      queryKey: ["review-queries", "get-jamb-result", user?.applicantId],
      queryFn: () => getJambByApplicantId(user?.applicantId ?? ""),
    },
    {
      queryKey: ["review-queries", "get-references", user?.applicantId],
      queryFn: async () => {
        try {
          return await getReferenceByApplicantId(user?.applicantId ?? "");
        } catch {
          return { data: { records: [], pageNumber: 1, pageSize: 0, totalRecords: 0, totalPages: 0 }, statusCode: 200, message: "" } as unknown as ReferenceResponse;
        }
      },
    },
  ],
});

  const institutionQuery = queries[0];
  const workHistoryQuery = queries[1];
  const nyscUploadQuery = queries[2];
  const personalInfoQuery = queries[3];
  const qualificationQuery = queries[4];
  const jambQuery = queries[5];
  const referenceQuery = queries[6];

  const personalInfoData =
    personalInfoQuery?.data?.data ?? ({} as PersonalInfo);
  const institutionData = institutionQuery?.data?.data ?? [];
  const workHistoryData = workHistoryQuery?.data?.data ?? [];
  const nyscUploadData = nyscUploadQuery?.data?.data ?? ({} as NyscUpload);
  const qualificationData = qualificationQuery?.data?.data ?? [];
  const jambData = jambQuery?.data?.data ?? ({} as JambResultData);
  const rawReferenceRecords = referenceQuery?.data?.data?.records;
  const referenceData = Array.isArray(rawReferenceRecords) ? (rawReferenceRecords as ReferenceData[]) : [];
  const uploadedRequiredDocuments = getStoredRequiredDocuments();
  const uploadedRequiredDocumentEntries = requiredDocuments
    ?.filter((item) => uploadedRequiredDocuments[`${item.id}`])
    .map((item) => ({
      ...item,
      uploadMeta: uploadedRequiredDocuments[`${item.id}`],
    })) ?? [];

  const isInstitutionLoading = institutionQuery?.isLoading;
  const isWorkHistoryLoading = workHistoryQuery?.isLoading;
  const isNyscUploadLoading = nyscUploadQuery?.isLoading;
  const isJambLoading = jambQuery?.isLoading;
  const isReferenceLoading = referenceQuery?.isLoading;

  const applyDiscountMutation = useMutation({
    mutationFn: getCouponDiscount,
  });

  const handleProcessPayment = async () => {
    let payload: ProcessPaymentPayloadWithChannel = {
      amount: 0,
      returnUrl: `${APP_BASE_URL}/payments`,
      applicantId: user?.applicantId ?? "",
      paymentType: 1,
    };
    if (couponCode) {
      payload.couponCode = couponCode;
    }
    initiatePayment(payload);
  };

  const handleApplyDiscount = async () => {
    setDiscountStatus("");
    const payload: CouponDiscountPayload = {
      couponCode: couponCode,
      applicantId: user?.applicantId ?? "",
    };

    try {
      await applyDiscountMutation?.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          setDiscountStatus("success");
        },
      });
    } catch (error: any) {
      setCouponCode("");
      setDiscountStatus("error");
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const isLateApplication = isLateApplicationPeriod();
  const discountAmount = applyDiscountMutation?.data?.data?.discountAmount ?? 0;
  const totalAmount =
    applyDiscountMutation?.data?.data?.discountedAmount ?? user?.amount ?? 0;

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const isValidDate = (date: string | null) => {
    return date !== null && date.includes("0001-01-01") === false;
  };

  const shouldShowInstitutionSection =
    user?.programType?.toLowerCase()?.includes("msc") ||
    user?.programType?.toLowerCase()?.includes("mba") ||
    (isBachelorApplicant(user?.programType) &&
      isDirectEntryApplicant(user?.modeOfEntry));

  const shouldShowUtmeSection =
    isUtmeApplicant(user?.modeOfEntry) && !!jambData?.examNumber;
  const shouldShowQualificationSection =
    !!user?.programType?.toLowerCase()?.includes("bsc");
  const shouldShowWorkHistorySection = workHistoryData?.length > 0;
  const shouldShowDocumentSection = Object.keys(nyscUploadData)?.length > 0;
  const shouldShowRequiredDocumentSection =
    uploadedRequiredDocumentEntries.length > 0;
  const shouldShowReferenceSection = referenceData?.length > 0;

  let nextSectionNumber = 2;
  const institutionSectionNumber = shouldShowInstitutionSection
    ? nextSectionNumber++
    : null;
  const qualificationSectionNumber = shouldShowQualificationSection
    ? nextSectionNumber++
    : null;
  const utmeSectionNumber = shouldShowUtmeSection ? nextSectionNumber++ : null;
  const workHistorySectionNumber = shouldShowWorkHistorySection
    ? nextSectionNumber++
    : null;
  const documentSectionNumber = shouldShowDocumentSection
    ? nextSectionNumber++
    : null;
  const referenceSectionNumber = shouldShowReferenceSection
    ? nextSectionNumber++
    : null;
  const requiredDocumentSectionNumber = shouldShowRequiredDocumentSection
    ? nextSectionNumber++
    : null;

  const handleExit = useCallback(() => {
  setUser({
    isNewUser: null,
    applicantId: "",
    token: "",
    programType: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    applicationName: "",
    sessionId: 0,
    sessionName: "", // ✅ added
    session: "", // ✅ added
    programTypeId: 0,
    amount: 0,
    earlyApplicationAmount: 0,
    lateApplicationAmount: 0,
  });

  localStorage.clear();

  navigate(routes.auth.landing);
}, [navigate, setUser]);

  if (
    isInstitutionLoading ||
    isWorkHistoryLoading ||
    isNyscUploadLoading ||
    isJambLoading ||
    isRequiredDocumentsLoading ||
    isReferenceLoading
  ) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <section className={styles.section}>
        <h5>Review</h5>
        <p>Please review your information before you proceed</p>
        <hr />
      </section>

      <div className={styles.reviewWrapper} ref={printRef}>
        <div className={styles.summary}>
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>Application Details</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  <div className={styles.institutionReview}>
                    <div>
                      <p>Program:</p>
                      <p>{user?.programName}</p>
                    </div>
                    <div>
                      <p>Program Type:</p>
                      <p>{user?.programType}</p>
                    </div>
                    <div>
                      <p>Mode of Study:</p>
                      <p>{user?.modeOfStudyName}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <ReviewPaymentDetails
            couponCode={couponCode}
            discountAmount={discountAmount}
            discountStatus={discountStatus}
            earlyApplicationAmount={user?.earlyApplicationAmount ?? 0}
            isApplyingDiscount={applyDiscountMutation?.isPending}
            isLateApplication={isLateApplication}
            lateApplicationFee={lateApplicationFee}
            originalTotalAmount={user?.amount ?? 0}
            shouldPayLateApplicationFee={shouldPayLateApplicationFee}
            totalAmount={totalAmount ?? 0}
            onApplyDiscount={handleApplyDiscount}
            onCouponCodeChange={setCouponCode}
          />
        </div>

        <section className={styles.reviewContainer}>
          <div>
            <div>
              <h5>1.</h5>
              <h5>Bio-data</h5>
            </div>

            <div>
              <div>
                <Divider className={styles.divider} type="vertical" />
                <Divider
                  className={styles.dashedDivider}
                  type="vertical"
                  dashed
                />
              </div>

              <div className={styles.textWrapper}>
                <div className={styles.image}>
                  <Image
                    width={120}
                    height={100}
                    src={personalInfoData?.imageUrl}
                    alt="review"
                    preview={{
                      width: "500",
                      height: "600",
                      visible,
                      scaleStep: 1,
                      src: personalInfoData?.imageUrl,
                      onVisibleChange: (value) => {
                        setVisible(value);
                      },
                    }}
                  />
                </div>

                <div>
                  <Button text="View Image" onClick={() => setVisible(true)} />
                </div>

                <div className={styles.institutionReview}>
                  <div>
                    <p>First Name:</p>
                    <p>{personalInfoData?.fName}</p>
                  </div>
                  <div>
                    <p>Middle Name:</p>
                    <p>{personalInfoData?.mNane}</p>
                  </div>
                  <div>
                    <p>Last Name:</p>
                    <p>{personalInfoData?.lName}</p>
                  </div>
                  <div>
                    <p>Gender:</p>
                    <p>{personalInfoData?.gender}</p>
                  </div>
                  <div>
                    <p>Phone No:</p>
                    <p>{personalInfoData?.phoneNo}</p>
                  </div>
                  <div>
                    <p>Email:</p>
                    <p>{personalInfoData?.email}</p>
                  </div>
                  <div>
                    <p>Address:</p>
                    <p>
                      {personalInfoData?.address} {personalInfoData?.zipCode}.
                    </p>
                  </div>
                  <div>
                    <p>Country:</p>
                    <p>{personalInfoData?.countryName}</p>
                  </div>
                  <div>
                    <p>Date Of Birth:</p>
                    <p>{formatDate(personalInfoData?.dateOfBirth)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {user && shouldShowInstitutionSection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{institutionSectionNumber}.</h5>
                <h5>Institution</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {institutionData?.map((item, index) => (
                    <div key={item?.id} className={styles.institutionReview}>
                      <div>
                        <p>Institution Attended:</p>
                        <p>{item?.institutionName}</p>
                      </div>

                      <div>
                        <p>Discipline:</p>
                        <p>{item?.discipline}</p>
                      </div>

                      <div>
                        <p>Year(start date):</p>
                        <p>{formatDate(item?.startDate)}</p>
                      </div>

                      <div>
                        <p>Year(end date):</p>
                        <p>{formatDate(item?.endDate)}</p>
                      </div>

                      <div>
                        <p>Grade:</p>
                        <p>{item?.cgpa}/5.0</p>
                      </div>

                      <div className={styles.docStyle}>
                        <FileIcon />
                        <a
                          href={item?.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.certificateLink}
                        >
                          View Certificate
                        </a>
                      </div>

                      {index + 1 < institutionData?.length && <hr />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {shouldShowQualificationSection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{qualificationSectionNumber}.</h5>
                <h5>Qualification</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {qualificationData &&
                    qualificationData?.map((item, index) => (
                      <div key={item?.id} className={styles.institutionReview}>
                        <div>
                          <p>Qualification Type:</p>
                          <p>{item?.qualificationType}</p>
                        </div>

                        <div>
                          <p>Exam Number:</p>
                          <p>{item?.examNumber}</p>
                        </div>

                        <div className={styles.qualification}>
                          <p>O'level Result:</p>

                          <div className={styles.subject}>
                            {item?.subjects?.map((subject) => (
                              <div>
                                <div>
                                  <p>{subject?.subject}</p>
                                  <p>{subject?.grade}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className={styles.docStyle}>
                          <FileIcon />

                          <a
                            href={item?.certificateiUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.certificateLink}
                          >
                            View Certificate
                          </a>
                        </div>

                        {index + 1 < qualificationData?.length && <hr />}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {shouldShowUtmeSection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{utmeSectionNumber}.</h5>
                <h5>UTME Result</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  <div className={styles.institutionReview}>
                    <div>
                      <p>Exam Number:</p>
                      <p>{jambData?.examNumber}</p>
                    </div>
                    <div>
                      <p>Exam Year:</p>
                      <p>{jambData?.examYear}</p>
                    </div>
                    <div>
                      <p>Total Score:</p>
                      <p>{jambData?.totalScore}</p>
                    </div>

                    {jambData?.subjects?.length > 0 && (
                      <div className={styles.qualification}>
                        <p>Subjects:</p>

                        <div className={styles.subject}>
                          {jambData.subjects.map((subject, index) => (
                            <div key={`${subject.subjectName}-${index}`}>
                              <div>
                                <p>{subject.subjectName}</p>
                                <p>{subject.score}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(jambData?.jambResultUrl || jambData?.jambResult) && (
                      <div className={styles.docStyle}>
                        <FileIcon />
                        <a
                          href={jambData?.jambResultUrl || jambData?.jambResult}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.certificateLink}
                        >
                          View JAMB Result
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {shouldShowWorkHistorySection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{workHistorySectionNumber}.</h5>
                <h5>Work History</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {workHistoryData?.map((item, index) => (
                    <div key={item?.id} className={styles.institutionReview}>
                      <div>
                        <p>Company:</p>
                        <p>{item?.companyName}</p>
                      </div>
                      <div>
                        <p>Role:</p>
                        <p>{item?.position}</p>
                      </div>
                      <div>
                        <p>Duration:</p>
                        <p>{`${formatDate(item?.startDate)} - ${
                          isValidDate(item?.endDate)
                            ? formatDate(item?.endDate)
                            : "Present"
                        }`}</p>
                      </div>

                      {index + 1 < workHistoryData?.length && <hr />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {shouldShowDocumentSection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{documentSectionNumber}.</h5>
                <h5>Document Upload</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  <div className={styles.docStyle}>
                    <p>NYSC Document</p>

                    <FileIcon />

                    <a
                      href={nyscUploadData?.nyscUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.certificateLink}
                    >
                      View Certificate
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {shouldShowReferenceSection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{referenceSectionNumber}.</h5>
                <h5>References</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {referenceData.map((item, index) => (
                    <div key={item.id} className={styles.institutionReview}>
                      <div>
                        <p>Name:</p>
                        <p>{item.title} {item.refereeName}</p>
                      </div>
                      <div>
                        <p>Email:</p>
                        <p>{item.refereeEmail}</p>
                      </div>
                      <div>
                        <p>Category:</p>
                        <p>
                          {[
                            { id: 0, name: "Supervisor" },
                            { id: 1, name: "Lecturer" },
                            { id: 2, name: "Professional" },
                          ].find((c) => c.id === item.refereeCategory)?.name}
                        </p>
                      </div>
                      {index + 1 < referenceData.length && <hr />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {shouldShowRequiredDocumentSection && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>{requiredDocumentSectionNumber}.</h5>
                <h5>Required Documents</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {uploadedRequiredDocumentEntries.map((item, index) => (
                    <div key={item.id} className={styles.docStyle}>
                      <p>{item.name}</p>

                      <FileIcon />

                      {item.uploadMeta.viewUrl ? (
                        <a
                          href={item.uploadMeta.viewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.certificateLink}
                        >
                          View {item.uploadMeta.fileName}
                        </a>
                      ) : (
                        <p>{item.uploadMeta.fileName}</p>
                      )}

                      {index + 1 < uploadedRequiredDocumentEntries.length && (
                        <hr />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      <RegistrationForm
        personalInfo={personalInfoData}
        institutions={institutionData}
        workHistory={workHistoryData}
        nyscUpload={nyscUploadData}
        qualifications={qualificationData}
        jambResult={jambData}
        references={referenceData}
        printRef={printRef}
        shouldPayLateApplicationFee={shouldPayLateApplicationFee}
        lateApplicationFee={lateApplicationFee}
        discountAmount={discountAmount}
        totalAmount={totalAmount}
      />

      <OnboardingButtonGroup
        onClick={handlePrevClick}
        onNextClick={handleProcessPayment}
        onExit={handleExit}
        onChangeCourse={() => setChangeCourseModalOpen(true)}
        isLoading={isProcessing}
        isReview={!user?.isPaymentSubmitted}
        onPrint={printDocument}
        hasPaid={user?.isPaymentSubmitted}
      />

      <ChangeCourseModal
        applicantId={user?.applicantId}
        open={changeCourseModalOpen}
        onClose={() => setChangeCourseModalOpen(false)}
      />
    </div>
  );
};

export default Review;
